import { abilitiesPlugin } from "@casl/vue";
import { QueryCache, QueryClient, VueQueryPlugin } from "@tanstack/vue-query";
import { createHead } from "@vueuse/head";
import { type AxiosError, isAxiosError } from "axios";
import { createPinia } from "pinia";
import piniaPluginPersistedstate from "pinia-plugin-persistedstate";
import { createApp } from "vue";
import "element-plus/dist/index.css";

import { ROUTE_NAMES as AUTH_ROUTE_NAMES } from "@/modules/auth";
import { useAuthStore } from "@/stores/auth";
import { useCoTransactionStore } from "@/stores/coTransactionStore";
import { HttpStatusCode } from "@/types/common";

import App from "./App.vue";
import router from "./router";
import { ability } from "./services/ability";

const pinia = createPinia();
pinia.use(piniaPluginPersistedstate);

const app = createApp(App);
const head = createHead();

router.afterEach(() => {
  const html = document.documentElement;
  html.classList.remove("dark");
});

// Register plugins
const plugins: any = import.meta.glob("./plugins/*.ts", { eager: true });

for (const path in plugins) {
  plugins[path].default({ app, router, head });
}

app.use(router);
app.use(pinia);

const coStore = useCoTransactionStore();

router.afterEach((to, from) => {
  if (
    from.name === "logs.logs.driver.transaction" &&
    to.name !== "logs.logs.driver.transaction"
  ) {
    coStore.clearTransaction();
  }
});

const isAxiosErorr = (error: unknown): error is AxiosError => {
  return error !== null && typeof error === "object" && "response" in error;
};

const isBillingRequiredError = (error: AxiosError) => {
  if (error.response?.status !== HttpStatusCode.FORBIDDEN) {
    return false;
  }

  const payload = (error.response?.data || {}) as {
    code?: number;
    errors?: { reason?: string; billingRequired?: boolean } | string;
  };
  const reason =
    typeof payload.errors === "object" && payload.errors
      ? payload.errors.reason
      : "";

  return (
    payload.code === 7006 ||
    reason === "billing_required" ||
    (typeof payload.errors === "object" && Boolean(payload.errors?.billingRequired))
  );
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: import.meta.env.PROD,
      retry(failureCount, error) {
        if (
          isAxiosErorr(error) &&
          error.response?.status === HttpStatusCode.FORBIDDEN
        ) {
          return false;
        }
        return failureCount < 3;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      if (isAxiosError(error)) {
        if (error.response?.status === HttpStatusCode.FORBIDDEN) {
          if (isBillingRequiredError(error)) {
            const currentPath = router.currentRoute.value.path;
            const isBillingPath = currentPath.startsWith("/billing") || currentPath.includes("/billing");
            const isAuthPath = currentPath.startsWith("/auth/");
            if (!isBillingPath && !isAuthPath) {
              router.push({ path: "/billing" });
            }
            return;
          }

          router.push({ name: AUTH_ROUTE_NAMES.Login });
          const authStore = useAuthStore();
          authStore.logOut();
        } else if (
          error.response?.status !== HttpStatusCode.UNPROCESSABLE_ENTITY
        ) {
          ElNotification({
            title: error.message,
            type: "error",
          });
        }
      }
    },
  }),
});
app.use(VueQueryPlugin, { queryClient });

app.use(abilitiesPlugin, ability, { useGlobalProperties: true });

app.mount("#app");
